/**
 * The fleet per hour of one day, as counts (never hours): busy, available,
 * production ready but offline, no readiness mark (and idle), not production
 * ready, and — on today — the hours that have not happened yet.
 */
import React from "react";
import { styled } from "styled-components";
import { HATCH, LANE_COLORS, LegendRow, Swatch } from "./lanes";
import { Timeline } from "./types";
import { tokens } from "../TractorState/tokens";

interface TractorsByHourProps {
  timelines: Timeline[];
  dayStart: string;
  dayEnd: string;
  nowIso?: string | null;
}

interface Counts {
  busy: number;
  available: number;
  readyOffline: number;
  /** No readiness mark in Tractor State, and not busy or available. */
  noMark: number;
  notReady: number;
  notYet: number;
}

function stateAt(timeline: Timeline, lane: string, t: number): string | null {
  const intervals =
    timeline.lanes.find((l) => l.lane === lane)?.intervals ?? [];
  const hit = intervals.find(
    (i) => Date.parse(i.start) <= t && t < Date.parse(i.end),
  );
  return hit?.state ?? null;
}

export function countsPerHour(
  timelines: Timeline[],
  dayStart: string,
  dayEnd: string,
  nowIso: string | null,
): Counts[] {
  const start = Date.parse(dayStart);
  const end = Date.parse(dayEnd);
  const now = nowIso ? Date.parse(nowIso) : null;
  const hours = Math.max(1, Math.round((end - start) / 3_600_000));
  return Array.from({ length: Math.min(hours, 25) }, (_, h) => {
    const t = start + h * 3_600_000;
    const counts: Counts = {
      busy: 0,
      available: 0,
      readyOffline: 0,
      noMark: 0,
      notReady: 0,
      notYet: 0,
    };
    for (const tl of timelines) {
      if (now !== null && t >= now) {
        counts.notYet += 1;
        continue;
      }
      const readiness = stateAt(tl, "readiness", t);
      if (readiness === "NOT_READY" || readiness === "BLOCKED") {
        counts.notReady += 1;
        continue;
      }
      const activity = stateAt(tl, "availability", t);
      if (activity === "BUSY") counts.busy += 1;
      else if (activity === "AVAILABLE") counts.available += 1;
      else if (readiness === "READY") counts.readyOffline += 1;
      else counts.noMark += 1;
    }
    return counts;
  });
}

const TractorsByHour: React.FC<TractorsByHourProps> = ({
  timelines,
  dayStart,
  dayEnd,
  nowIso = null,
}) => {
  const total = timelines.length;
  const columns = countsPerHour(timelines, dayStart, dayEnd, nowIso);
  const px = (n: number) => (total ? Math.round((n / total) * 120) : 0);
  return (
    <Card>
      <Head>
        <Title>Tractors by hour · {total} in the day</Title>
        <LegendRow>
          <span>
            <Swatch $cls="b" /> Busy
          </span>
          <span>
            <Swatch $cls="a" /> Available
          </span>
          <span>
            <Swatch $cls="f" /> Production ready, offline
          </span>
          <span>
            <Swatch $cls="u" /> No mark
          </span>
          <span>
            <Swatch $cls="n" /> Not production ready
          </span>
          <span>
            <Swatch $cls="t" /> Not yet
          </span>
        </LegendRow>
      </Head>
      <Columns
        role="img"
        aria-label={`Tractors per hour by state, ${total} tractors`}
      >
        {columns.map((c, h) => (
          <Column
            key={h}
            data-testid="hour"
            data-counts={`busy=${c.busy} available=${c.available} ready-offline=${c.readyOffline} no-mark=${c.noMark} not-ready=${c.notReady} not-yet=${c.notYet}`}
            title={`${String(h).padStart(2, "0")}:00 · busy ${c.busy} · available ${c.available} · ready, offline ${c.readyOffline} · no mark ${c.noMark} · not ready ${c.notReady}`}
          >
            <span style={{ height: px(c.busy), background: LANE_COLORS.b }} />
            <span
              style={{ height: px(c.available), background: LANE_COLORS.a }}
            />
            <span
              style={{ height: px(c.readyOffline), background: LANE_COLORS.f }}
            />
            <span style={{ height: px(c.noMark), background: LANE_COLORS.u }} />
            <span
              style={{ height: px(c.notReady), background: LANE_COLORS.n }}
            />
            <span style={{ height: px(c.notYet), background: HATCH }} />
          </Column>
        ))}
      </Columns>
      <Axis aria-hidden="true">
        {[0, 4, 8, 12, 16, 20, 24].map((h) => (
          <span key={h}>{String(h).padStart(2, "0")}:00</span>
        ))}
      </Axis>
    </Card>
  );
};

export default TractorsByHour;

const Card = styled.div`
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px 16px;
`;

const Title = styled.h2`
  margin: 0;
  font: 600 14px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(24, minmax(0, 1fr));
  gap: 2px;
  align-items: end;
  height: 120px;
  border-bottom: 1px solid ${tokens.line};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 1px;
  height: 100%;
  justify-content: flex-start;
  > span {
    display: block;
    border-radius: 2px;
  }
`;

const Axis = styled.div`
  display: flex;
  justify-content: space-between;
  font: 11px ${tokens.fontMono};
  color: ${tokens.slate};
`;
